import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = () => (
  <a href="/" className="inline-flex items-baseline cursor-pointer hover:opacity-80 transition-opacity">
    <span className="text-xl font-black text-[#0F172A] font-['Outfit'] tracking-tight">ORDO AI</span>
  </a>
);

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotOpen, setForgotOpen]     = useState(false);
  const [forgotEmail, setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent]     = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim())    { toast.error("Enter your email");    return; }
    if (!password.trim()) { toast.error("Enter your password"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        if (data.session_id) localStorage.setItem("session_id", data.session_id);
        toast.success("Welcome back!");
        navigate("/auth/redirect");
      } else {
        toast.error(data.detail || data.error || "Invalid email or password");
      }
    } catch {
      toast.error("Connection error. Please try again");
    } finally {
      setLoading(false);
    }
  };

  // ── FIXED: Use backend endpoint — frontend redirect_uri causes 400 ──
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const res = await fetch(`${API}/auth/google/url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_uri: "https://rankfirst-production.up.railway.app/api/auth/google/callback",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        toast.error("Failed to start Google login");
        setGoogleLoading(false);
      }
    } catch {
      toast.error("Error starting Google login");
      setGoogleLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Enter your email"); return; }
    setForgotLoading(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      setForgotSent(true);
    } catch {
      toast.error("Something went wrong. Try again");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Outfit'] flex flex-col">

      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 h-16 flex items-center px-6">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
          <Logo />
          <a href="/pricing" className="text-sm font-medium text-slate-600 hover:text-[#1E40AF] transition-colors">
            View Pricing
          </a>
        </div>
      </nav>

      {/* MAIN */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="h-1.5 bg-[#1E40AF]" />
            <div className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-black text-[#0F172A] font-['Outfit'] mb-1">Welcome back</h1>
                <p className="text-slate-500 text-sm">Log in to your ORDO AI account</p>
              </div>

              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 h-12 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors mb-6 disabled:opacity-60">
                {googleLoading
                  ? <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  : <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                }
                Continue with Google
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Email form */}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.com"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-[#0F172A]">Password</label>
                    <button type="button" onClick={() => setForgotOpen(true)}
                      className="text-xs text-[#1E40AF] hover:underline font-medium">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full h-12 px-4 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold h-12 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 mt-2">
                  {loading
                    ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <>Log In <ArrowRight className="w-4 h-4" /></>
                  }
                </button>
              </form>
            </div>
          </div>

          {/* Signup CTA */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500 mb-3">Don't have an account?</p>
            <a href="/"
              className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white font-bold px-6 py-3 rounded-xl text-sm transition-colors">
              Get Your Free ORDO AI Score <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-xs text-slate-400 mt-2">3 days free trial, no set up fees</p>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200 py-5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <a href="/pricing" className="hover:text-[#1E40AF] transition-colors">Pricing</a>
            <a href="mailto:support@theordo.io" className="hover:text-[#1E40AF] transition-colors">support@theordo.io</a>
          </div>
          <p className="text-xs text-slate-400">© 2026 ORDO AI LLC</p>
        </div>
      </footer>

      {/* FORGOT PASSWORD MODAL */}
      {forgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-[#1E40AF] px-6 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold font-['Outfit']">Reset Password</h3>
              <button onClick={() => { setForgotOpen(false); setForgotSent(false); setForgotEmail(""); }}
                className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              {forgotSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-semibold text-[#0F172A] mb-1 font-['Outfit']">Check your email</p>
                  <p className="text-sm text-slate-500">
                    If an account exists for <strong>{forgotEmail}</strong>, you'll receive a reset link shortly
                  </p>
                  <button onClick={() => { setForgotOpen(false); setForgotSent(false); setForgotEmail(""); }}
                    className="mt-5 w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold py-3 rounded-xl text-sm transition-colors">
                    Back to Log In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <p className="text-sm text-slate-600">Enter your email and we'll send a reset link</p>
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black"
                      autoFocus
                    />
                  </div>
                  <button type="submit" disabled={forgotLoading}
                    className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                    {forgotLoading
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : "Send Reset Link"
                    }
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
